import { useCallback } from 'react';
import { v4 } from 'uuid';
import { pm2SitemapStore, findPageDescriptorByPath } from '@pm2/sitemap-utils';
import { pm2ConfigStore } from '@pm2/store';
import { useLocation, useNavigationManager } from '@provider/navigation';
import { useNotifier } from '@provider/notifier';
import { useUserIdentityState } from '@provider/user-identity';
import { useCreateRevisionMutation } from '../api/mutation.create.js';
import { useUpdateRevisionMutation } from '../api/mutation.update.js';
import { makeLeaveExistingRevisionModalConfig } from '../modals/leave.existing.js';
import { makeLeaveNewRevisionModalConfig } from '../modals/leave.new.js';
import { useCourseRevisionState, courseRevisionsStore } from '../store.js';
import { disabledIdsToKey } from '../util.disabled-ids-to-key.js';
import { getFirstValidChapterDescriptorInCourse } from '../util.get-first-valid-chapter-descriptor-in-course.js';

function useLeaveRevision(courseDescriptor) {
  const portalId = pm2ConfigStore.useSelector(state => state.config.id);
  const sitemapState = pm2SitemapStore.useSelector(state => state);
  const location = useLocation();
  const notifier = useNotifier();
  const user = useUserIdentityState(state => state.user);
  const navigation = useNavigationManager();
  const editedRevision = useCourseRevisionState(state => state.editableRevision);
  const hasMadeChanges = useCourseRevisionState(state => disabledIdsToKey(state.disabledIds) !== disabledIdsToKey(state.editableRevision?.disabledContentIds));
  const createMutation = useCreateRevisionMutation();
  const updateMutation = useUpdateRevisionMutation();
  const chapterDescriptor = findPageDescriptorByPath(sitemapState, location.pathname);
  return useCallback(async options => {
    const state = courseRevisionsStore.getState();
    if (!state.isEditing) {
      return "not-editing";
    }
    if (!courseDescriptor) {
      return "invalid";
    }
    if (!hasMadeChanges) {
      const firstValidChapter = getFirstValidChapterDescriptorInCourse(editedRevision, courseDescriptor);
      if (chapterDescriptor && editedRevision?.disabledContentIds?.[chapterDescriptor.id]?.disabled && firstValidChapter) {
        const unsubscribe = navigation.subscribe(() => {
          const location = navigation.getLocation();
          if (location.pathname === firstValidChapter.path) {
            courseRevisionsStore.dispatch(courseRevisionsStore.actions.toggleEditMode({
              state: false
            }));
            unsubscribe();
          }
        });
        navigation.replace(firstValidChapter.path);
      } else {
        courseRevisionsStore.dispatch(courseRevisionsStore.actions.toggleEditMode({
          state: false
        }));
      }
      return "no-changes";
    }
    const newDisabledIds = courseRevisionsStore.getState().disabledIds;

    //
    // STEP: Resolve the revision that should be saved, including relevant
    // metadata.
    //
    const nextRevision = await (async () => {
      if (editedRevision) {
        switch ((await notifier.modal(makeLeaveExistingRevisionModalConfig({
          ...options,
          revision: editedRevision
        })))?.intent) {
          case "save":
            return {
              method: "update",
              revision: {
                ...editedRevision,
                disabledContentIds: newDisabledIds
              }
            };
          case "do-not-save":
            {
              return {
                method: "do-not-save"
              };
            }
          default:
            return {
              method: "cancel"
            };
        }
      } else {
        const id = v4();
        const newModalConfig = await notifier.modal(makeLeaveNewRevisionModalConfig(options));
        if (!newModalConfig) {
          return {
            method: "cancel"
          };
        }
        if (newModalConfig.intent === "do-not-save") {
          return {
            method: "do-not-save"
          };
        }
        return {
          method: "create",
          revision: {
            id,
            __clientId: id,
            __syncedWithBackend: false,
            courseId: courseDescriptor.id,
            portalId,
            userId: user?.id ?? "",
            name: newModalConfig.title,
            disabledContentIds: newDisabledIds,
            isActive: true
          }
        };
      }
    })();
    if (nextRevision.method === "cancel") {
      return "cancel";
    }

    //
    // STEP: Perform save including relevant side effects such as navigation,
    // etc.
    //
    const firstValidChapter = getFirstValidChapterDescriptorInCourse(nextRevision.revision ?? editedRevision, courseDescriptor);
    const applyRevision = () => {
      switch (nextRevision.method) {
        case "create":
          createMutation.mutate(nextRevision.revision);
          break;
        case "update":
          updateMutation.mutate(nextRevision.revision);
      }
      courseRevisionsStore.dispatch(courseRevisionsStore.actions.toggleEditMode({
        state: false
      }));
    };
    const nextDisabledContentIds = nextRevision.method === "do-not-save" ? editedRevision?.disabledContentIds ?? {} : newDisabledIds;

    // In case the chapter that the user is currently on has been removed in
    // the most recent revision, then automatically navigate them to the
    // nearest chapter that is still present.
    if (chapterDescriptor && nextDisabledContentIds[chapterDescriptor.id]?.disabled && firstValidChapter) {
      // ... and ensure that the navigation has been fully completed before
      // applying the revision to avoid flashes of content before navigation
      // ends.
      const unsubscribe = navigation.subscribe(() => {
        const location = navigation.getLocation();
        if (location.pathname === firstValidChapter.path) {
          applyRevision();
          unsubscribe();
        }
      });
      navigation.replace(firstValidChapter.path);
    } else {
      applyRevision();
    }
    return nextRevision.method;
  }, [chapterDescriptor, courseDescriptor, createMutation, editedRevision, hasMadeChanges, navigation, notifier, portalId, updateMutation, user?.id]);
}

export { useLeaveRevision };
//# sourceMappingURL=leave.js.map
